import React from 'react';
import { getBuildStamp } from '@/lib/buildStamp';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export class GlobalErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, componentStack: null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('THERAPPI_FATAL', error, info);
    this.setState({ componentStack: info.componentStack || null });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const route = typeof window !== 'undefined' ? window.location.pathname : '/';
      const stamp = getBuildStamp(route);

      return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-background p-6">
          <div className="max-w-md w-full text-center space-y-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              An unexpected error occurred. Please reload the app.
            </p>
            {this.state.error && (
              <details className="text-left" open>
                <summary className="text-xs text-muted-foreground cursor-pointer">Error details</summary>
                <pre className="mt-2 p-2 bg-muted/30 rounded text-[10px] leading-tight overflow-auto max-h-64 whitespace-pre-wrap">
                  {this.state.error.message}
                  {this.state.error.stack ? `\n\n${this.state.error.stack}` : ''}
                  {this.state.componentStack ? `\n\nComponent stack:${this.state.componentStack}` : ''}
                </pre>
              </details>
            )}
            <Button onClick={this.handleReload} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Reload App
            </Button>
            <p className="text-[9px] font-mono text-muted-foreground/50 pt-2">{stamp}</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
